// Parsing for live handoff's two message layers (docs/adr/0010): the
// room's JSON socket frames, and the WebRTC signals carried inside sealed
// `signal` records. Everything here is untrusted input from the network or
// the other browser, so anything unexpected becomes null.

import { HANDOFF_KIND_HELLO, HANDOFF_KIND_RECORD, HANDOFF_VERSION } from '@sss/crypto';
import type { RelayIceServer, RoomEndReason, RoomServerFrame } from '@sss/types';

const END_REASONS: ReadonlySet<string> = new Set<RoomEndReason>(['acknowledged', 'delivered', 'cancelled', 'expired', 'senderLeft', 'relayLimit']);
type RoomErrorType = Extract<RoomServerFrame, { t: 'error' }>['type'];
const ERROR_TYPES: ReadonlySet<string> = new Set<RoomErrorType>(['badFrame', 'unauthorized', 'tooLarge']);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const parseServerFrame = (data: unknown): RoomServerFrame | null => {
  if (typeof data !== 'string') return null;
  let frame: unknown;
  try {
    frame = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(frame)) return null;
  const { t, role, expiresAt, present, d, reason, type } = frame;
  switch (t) {
    case 'joined':
      return (role === 'sender' || role === 'recipient') && typeof expiresAt === 'string' ? { t, role, expiresAt } : null;
    case 'peer':
      return typeof present === 'boolean' && typeof expiresAt === 'string' ? { t, present, expiresAt } : null;
    case 'relay':
      return typeof d === 'string' ? { t, d } : null;
    case 'ended':
      return typeof reason === 'string' && END_REASONS.has(reason) ? { t, reason: reason as RoomEndReason } : null;
    case 'unavailable':
      return { t };
    case 'error':
      return typeof type === 'string' && ERROR_TYPES.has(type) ? { t, type: type as RoomErrorType } : null;
    default:
      return null;
  }
};

/** What a relayed blob is, from its first two bytes (docs/crypto-spec.md §5.3, §5.5). */
export const blobKind = (bytes: Uint8Array): 'hello' | 'record' | null => {
  if (bytes[0] !== HANDOFF_VERSION) return null;
  if (bytes[1] === HANDOFF_KIND_HELLO) return 'hello';
  if (bytes[1] === HANDOFF_KIND_RECORD) return 'record';
  return null;
};

/** WebRTC signalling, as JSON inside sealed `signal` records. */
export type Signal =
  /** The sender's offer may carry a paid TURN relay for the recipient to use too (docs/adr/0013). */
  | { description: RTCSessionDescriptionInit; relay?: RelayIceServer }
  | { candidate: RTCIceCandidateInit }
  /** "I can't do WebRTC": the sender should use the room at once instead of waiting. */
  | { unsupported: true };

export const parseSignal = (body: Uint8Array): Signal | null => {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(body));
  } catch {
    return null;
  }
  if (!isRecord(value)) return null;
  const { description, candidate, unsupported, relay } = value;
  if (isRecord(description) && (description.type === 'offer' || description.type === 'answer') && typeof description.sdp === 'string') {
    const signal: Signal = { description: { type: description.type, sdp: description.sdp } };
    if (relay === undefined) return signal;
    const parsed = description.type === 'offer' ? parseRelay(relay) : null;
    return parsed ? { ...signal, relay: parsed } : null;
  }
  if (isRecord(candidate) && typeof candidate.candidate === 'string') {
    const { sdpMid, sdpMLineIndex, usernameFragment } = candidate;
    return {
      candidate: {
        candidate: candidate.candidate,
        sdpMid: typeof sdpMid === 'string' ? sdpMid : null,
        sdpMLineIndex: typeof sdpMLineIndex === 'number' ? sdpMLineIndex : null,
        usernameFragment: typeof usernameFragment === 'string' ? usernameFragment : null,
      },
    };
  }
  if (unsupported === true) return { unsupported: true };
  return null;
};

/**
 * Only Cloudflare's TURN servers are accepted from the other browser: a
 * relay elsewhere would let the sender point the recipient's browser (and
 * its address) at a server of their choosing.
 */
const RELAY_URL = /^turns?:turn\.cloudflare\.com(:\d{1,5})?(\?transport=(udp|tcp))?$/;
const MAX_RELAY_URLS = 8;
const MAX_RELAY_FIELD_CHARS = 512;

const parseRelay = (value: unknown): RelayIceServer | null => {
  if (!isRecord(value)) return null;
  const { urls, username, credential } = value;
  if (!Array.isArray(urls) || urls.length === 0 || urls.length > MAX_RELAY_URLS) return null;
  if (!urls.every((url): url is string => typeof url === 'string' && RELAY_URL.test(url))) return null;
  if (typeof username !== 'string' || username.length === 0 || username.length > MAX_RELAY_FIELD_CHARS) return null;
  if (typeof credential !== 'string' || credential.length === 0 || credential.length > MAX_RELAY_FIELD_CHARS) return null;
  return { urls, username, credential };
};

/**
 * Whether the connection's selected candidate pair goes through a TURN
 * relay, from `RTCPeerConnection.getStats()`. Chrome and Safari name the
 * pair on the transport; Firefox marks it `selected`.
 */
export const selectedPairUsesRelay = (stats: Iterable<Record<string, unknown>>): boolean => {
  const reports = [...stats];
  const byId = new Map(reports.map((r) => [r.id, r]));
  const selectedId = reports.find((r) => r.type === 'transport' && typeof r.selectedCandidatePairId === 'string')?.selectedCandidatePairId;
  const pair = reports.find(
    (r) => r.type === 'candidate-pair' && (selectedId !== undefined ? r.id === selectedId : r.selected === true || (r.nominated === true && r.state === 'succeeded')),
  );
  if (!pair) return false;
  return [pair.localCandidateId, pair.remoteCandidateId].some((id) => byId.get(id)?.candidateType === 'relay');
};

export const encodeSignal = (signal: Signal): Uint8Array => new TextEncoder().encode(JSON.stringify(signal));

/** "042917" → "042 917", easier to read aloud. */
export const formatCode = (code: string): string => `${code.slice(0, 3)} ${code.slice(3)}`;
